import { ActivityIndicator, StyleSheet, View } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import FormLayout from "../common/FormLayout";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { Recycle, Sparkle } from "@phosphor-icons/react";
import {
  useCreateWorksheetMutation,
  useGetWorksheetQuery,
  WorksheetGenerationStatus,
} from "../../../../graphql/generated/graphql";
import { useState, useEffect } from "react";
import useFileFetch from "../../../../hooks/useFileFetch";

const schema = z.object({
  coverage: z.string().min(1, "Coverage is required"),
  title: z.string().min(1, "Title is required"),
  instructions: z.string().optional(),
  numQuestions: z.number().min(1, "Question count is required"),
});

const WorksheetForm = ({ onClose, onSubmit, activityData, setValue, pdfUrl, setPdfUrl }) => {
  const { fetchAndOpenFile } = useFileFetch();

  const [createWorksheet, { loading: createWorksheetLoading }] = useCreateWorksheetMutation();

  const [worksheetId, setWorksheetId] = useState(activityData?.worksheet?.id);

  const {
    data: worksheetData,
    startPolling,
    stopPolling,
  } = useGetWorksheetQuery({
    variables: { id: worksheetId || "" },
    skip: !worksheetId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const status = worksheetData?.getWorksheet?.data?.generationStatus;
    if (status === WorksheetGenerationStatus.Completed) {
      setPdfUrl(worksheetData.getWorksheet.data.pdfUrl);
      stopPolling();
    } else if (status === WorksheetGenerationStatus.Failed) {
      stopPolling();
    }
  }, [worksheetData, stopPolling, setPdfUrl]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      coverage: activityData?.worksheet?.coverage || "",
      title: activityData?.worksheet?.title || "",
      instructions: activityData?.worksheet?.instructions || "",
      numQuestions: activityData?.worksheet?.numQuestions || 1,
    },
    resolver: zodResolver(schema),
  });

  const handleCreateAssignmentWithWorksheet = async () => {
    await onSubmit();
  };

  const handleCreateWorksheet = handleSubmit(async (data) => {
    const result = await createWorksheet({
      variables: {
        worksheet: data,
        existingWorksheetId: activityData?.worksheet?.id,
      },
    });

    if (result.data?.createWorksheet.success) {
      setValue("worksheetId", result.data.createWorksheet.data.worksheetId);
      setWorksheetId(result.data.createWorksheet.data.worksheetId);
      startPolling(5000);
    } else {
      console.error(result.data?.createWorksheet.message);
    }
  });

  useEffect(() => {
    if (worksheetId) {
      startPolling(5000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [worksheetId, startPolling, stopPolling]);

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Assignment with Worksheet`}
      onCancel={onClose}
      onSubmit={handleCreateAssignmentWithWorksheet}
      isSubmitting={
        createWorksheetLoading ||
        worksheetData?.getWorksheet?.data?.generationStatus === WorksheetGenerationStatus.Processing
      }
      actionText={activityData?.id ? "Save Assignment with Worksheet" : "Create Assignment with Worksheet"}
      error={undefined}>
      <CustomText text="What should the worksheet cover?" />
      <CustomText
        text="Describe the concepts, skills, or topics you'd like the worksheet to reinforce. We’ll generate sample questions you can edit before attaching."
        size="s"
      />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="coverage"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter coverage"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
            multiline={true}
            numberOfLines={4}
          />
        )}
      />
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="Worksheet Title" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="title"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter title"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
          />
        )}
      />
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="Instructions (Optional)" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="instructions"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter instructions for students to consider while working on the assignment"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
            multiline={true}
            numberOfLines={4}
          />
        )}
      />
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="How many questions should the worksheet have?" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="numQuestions"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter question count"
            keyboardType="numeric"
            onBlur={onBlur}
            onChangeText={(text) => {
              if (text === "") {
                onChange("");
                return;
              }
              const num = parseInt(text, 10);
              if (!isNaN(num)) {
                onChange(num);
              }
            }}
            value={value !== undefined && value !== null ? String(value) : ""}
            style={{ width: "fit-content" }}
          />
        )}
      />
      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.buttonContainer}>
        <CustomButton
          leftIcon={
            createWorksheetLoading ||
            worksheetData?.getWorksheet?.data?.generationStatus === WorksheetGenerationStatus.Processing ? (
              <ActivityIndicator size={24} color="#3E68FE" />
            ) : worksheetId ? (
              <Recycle size={24} color="#3E68FE" weight="bold" />
            ) : (
              <Sparkle size={24} color="#3E68FE" weight="bold" />
            )
          }
          text={
            createWorksheetLoading ||
            worksheetData?.getWorksheet?.data?.generationStatus === WorksheetGenerationStatus.Processing
              ? "Generating Worksheet..."
              : worksheetId
                ? "Regenerate Worksheet"
                : "Generate Worksheet"
          }
          onPress={handleCreateWorksheet}
          styleType="primaryTransparentStroke"
          disabled={
            createWorksheetLoading ||
            worksheetData?.getWorksheet?.data?.generationStatus === WorksheetGenerationStatus.Processing
          }
        />
        {worksheetId && pdfUrl && (
          <CustomButton
            text="Download Worksheet Preview"
            onPress={() => fetchAndOpenFile(pdfUrl)}
            styleType="primaryTransparent"
            disabled={
              createWorksheetLoading ||
              worksheetData?.getWorksheet?.data?.generationStatus === WorksheetGenerationStatus.Processing
            }
          />
        )}
      </View>
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-end",
    gap: 16,
  },
});

export default WorksheetForm;
